/**
 * Axios client with JWT authentication interceptor.
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,  // 2 minutes (increased from 30s to handle slower API calls)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage
const TOKEN_KEY = 'bsp_access_token';
const REFRESH_TOKEN_KEY = 'bsp_refresh_token';
// 登录时下发的 Keycloak access token。后端需要它以「当前登录用户」身份调用
// VE 车型查询等受 Keycloak 保护的外部接口。会话期间不刷新（BSP JWT 先过期，
// 重新登录即可拿到新的）。
const VE_TOKEN_KEY = 'bsp_ve_token';

export const getAccessToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const getVeToken = (): string | null => {
  return localStorage.getItem(VE_TOKEN_KEY);
};

export const setVeToken = (token: string | null | undefined): void => {
  if (token) {
    localStorage.setItem(VE_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(VE_TOKEN_KEY);
  }
};

export const setTokens = (access: string, refresh: string): void => {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
};

export const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(VE_TOKEN_KEY);
};

// Cross-tab token sync via BroadcastChannel.
// When one tab refreshes the JWT, it broadcasts the new tokens so other tabs
// update their localStorage immediately — preventing them from trying to use
// an already-rotated (and blacklisted) refresh token.
const tokenChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('bsp_token_sync')
    : null;

tokenChannel?.addEventListener('message', (event: MessageEvent) => {
  if (event.data?.type === 'TOKEN_REFRESHED') {
    setTokens(event.data.access, event.data.refresh);
  }
});

// Extend config type to carry per-request metadata used by the 401 handler
type RequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _reqRefreshToken?: string | null;
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: RequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Snapshot the refresh token at request time so the 401 handler can detect
    // whether another tab already performed a refresh while this request was in-flight.
    config._reqRefreshToken = getRefreshToken();
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RequestConfig;

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // ── Cross-tab detection ───────────────────────────────────────────────
      // If the refresh token in localStorage changed since this request was sent,
      // another tab already performed a refresh. Retry immediately with the new
      // access token instead of issuing a duplicate refresh (which would fail
      // because the old refresh token was already rotated and blacklisted).
      const storedRefresh = getRefreshToken();
      if (
        storedRefresh &&
        originalRequest._reqRefreshToken &&
        originalRequest._reqRefreshToken !== storedRefresh
      ) {
        const newAccess = getAccessToken();
        if (newAccess && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        }
        return apiClient(originalRequest);
      }
      // ─────────────────────────────────────────────────────────────────────

      if (isRefreshing) {
        // Queue request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
          refresh: refreshToken,
        });

        const { access, refresh } = response.data;
        setTokens(access, refresh);
        // Notify other tabs so they skip their own refresh and use these tokens.
        tokenChannel?.postMessage({ type: 'TOKEN_REFRESHED', access, refresh });
        processQueue(null, access);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Last chance: check if another tab refreshed successfully while our
        // refresh request was in-flight (race condition on the POST itself).
        const latestRefresh = getRefreshToken();
        if (
          latestRefresh &&
          originalRequest._reqRefreshToken &&
          latestRefresh !== originalRequest._reqRefreshToken
        ) {
          const latestAccess = getAccessToken();
          processQueue(null, latestAccess ?? null);
          isRefreshing = false;
          if (latestAccess && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${latestAccess}`;
          }
          return apiClient(originalRequest);
        }
        processQueue(refreshError, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
